// src/config/fetchRemoteConfig.ts

import { setRemoteEnv, RemoteEnv } from "./remoteEnv";

const SESSION_KEY_PREFIX = "remoteEnvConfig";
const CACHE_TTL_MS = 60_000; // 1 minute — whole config (incl. menu) revalidates after this.

export const fetchRemoteConfig = async (): Promise<void> => {
  // Scope the cache by exactly what determines the config response:
  // projectId on /r/ routes, hostname on custom domains.
  //
  // REACT_APP_PINNED_APP_ID (unset for the web build) pins the brand at build
  // time — the Capacitor store apps (mobile/ on the `mobile` branch) load from
  // capacitor://localhost / https://localhost, where neither the path nor the
  // hostname identifies the brand.
  const pinnedAppId = process.env.REACT_APP_PINNED_APP_ID;
  const urlMatch = pinnedAppId
    ? ["", pinnedAppId]
    : window.location.pathname.match(/\/r\/([^/]+)/);
  const configScope = urlMatch ? urlMatch[1] : window.location.hostname;
  const sessionKey = `${SESSION_KEY_PREFIX}_${configScope}`;

  // Return early if cached AND still within the TTL.
  const cached = sessionStorage.getItem(sessionKey);
  if (cached) {
    try {
      const { env, fetchedAt } = JSON.parse(cached);
      if (env && Date.now() - fetchedAt < CACHE_TTL_MS) {
        setRemoteEnv(env);
        console.log("[RemoteConfig] Loaded from sessionStorage cache (fresh)");
        return;
      }
      // Stale — drop it and re-fetch below.
      sessionStorage.removeItem(sessionKey);
    } catch {
      console.warn("[RemoteConfig] Failed to parse cached config, re-fetching");
      sessionStorage.removeItem(sessionKey);
    }
  }

  const configUrl = process.env.REACT_APP_CONFIG_URL;
  const configToken = process.env.REACT_APP_CONFIG_TOKEN;

  if (!configUrl) throw new Error("[RemoteConfig] REACT_APP_CONFIG_URL is not set");
  if (!configToken) throw new Error("[RemoteConfig] REACT_APP_CONFIG_TOKEN is not set");

  const queryParam = urlMatch
    ? `projectId=${configScope}`
    : `overrideHost=${configScope}`;

  const response = await fetch(`${configUrl}?${queryParam}`, {
    headers: {
      Authorization: `Bearer ${configToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(
      `[RemoteConfig] Failed to fetch config: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();
  const env = data?.config?.env;

  if (!env) {
    throw new Error("[RemoteConfig] Response did not contain config.env");
  }

  const remoteEnv: RemoteEnv = {
    appId: env.appId,
    appName: env.appName,
    region: env.region,
    theme: env.theme,
    themeOverrideAccentColor: env.theme_override_accent_color,
    themeOverrideNavbarColor: env.theme_override_navbar_color || undefined,
    themeCustom: env.theme_custom || undefined,
    appType: env.appType,
    menuOnly: env.menu_only === true,
    fullCatalogueView: env.full_catalogue_view === true,
    countryCode: env.country_code,
    currencyCode: env.currency_code,
    currencySymbol: env.currency_symbol,
    stripeTestKey: env.stripe_test_key,
    stripeLiveKey: env.stripe_live_key,
    appIconUrl: env.appIconUrl,
    logoUrl: env.logoUrl,
    homeStoreUrl: env.homeStoreUrl,
    terms: env.terms,
    privacyPolicy: env.privacy_policy,
    firebaseConfig: {
      apiKey: env.firebase_config.apiKey,
      authDomain: env.firebase_config.authDomain,
      projectId: env.firebase_config.projectId,
      storageBucket: env.firebase_config.storageBucket,
      messagingSenderId: env.firebase_config.messagingSenderId,
      appId: env.firebase_config.appId,
      measurementId: env.firebase_config.measurementId,
    },
  };

  try {
    const res = await fetch(
      `https://www.dineplatform.com/r/${remoteEnv.appId}/restaurants.json?_=${Date.now()}`
    );
    if (res.ok) {
      const data = await res.json();
      remoteEnv.restaurantData = data;
    }
  } catch (e) {
    console.warn("[RemoteConfig] Failed to fetch restaurant data:", e);
  }

  sessionStorage.setItem(
    sessionKey,
    JSON.stringify({ env: remoteEnv, fetchedAt: Date.now() })
  );
  setRemoteEnv(remoteEnv);
  console.log("[RemoteConfig] Fetched and cached config for:", remoteEnv.appId);
};